import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Shell from '../Shell';
import { api } from '../api';

const ORDER = [
  'New', 'Structured', 'SentToSuppliers', 'AwaitingQuotes', 'PartialQuotes',
  'ReadyForComparison', 'DecisionMade', 'CustomerQuoteSent', 'Won', 'Lost',
];

const QUOTED_STATUSES = new Set(['AllQuoted', 'Decided', 'CustomerQuoted', 'Won']);

export default function Dashboard() {
  const [kpis, setKpis] = useState(null);
  // Phase F: line-level KPIs computed from open RFQs
  const [openRFQs, setOpenRFQs] = useState([]);

  useEffect(() => {
    api.kpis().then(setKpis);
    // Fetch RFQs for line-level KPIs (filter to active statuses client-side)
    api.listRFQs({ pageSize: 200 })
      .then((r) => {
        const active = (r.items || []).filter((rfq) =>
          !['Won', 'Lost', 'Archived'].includes(rfq.status)
        );
        setOpenRFQs(active);
      })
      .catch(() => {});
  }, []);

  // Phase F: Line-level KPI derivations
  const lineKpis = useMemo(() => {
    if (!openRFQs.length) return null;
    let totalLines = 0;
    let quotedLines = 0;
    let partialDecisionRFQs = 0;
    const agingLines = [];
    const now = Date.now();

    for (const rfq of openRFQs) {
      const lines = rfq.lineItems || rfq.lines || [];
      if (!lines.length) continue;
      let decidedCount = 0;
      let notDecidedCount = 0;
      totalLines += lines.length;
      for (const line of lines) {
        if (QUOTED_STATUSES.has(line.lineStatus)) {
          quotedLines++;
          decidedCount++;
        } else {
          notDecidedCount++;
          // Aging: Dispatched for > 48h with no quote
          if (line.lineStatus === 'Dispatched' && line.dispatchedAt) {
            const ageH = (now - new Date(line.dispatchedAt).getTime()) / 3600000;
            if (ageH > 48) {
              agingLines.push({ rfq, line, ageH: Math.round(ageH) });
            }
          }
        }
      }
      // Partial decision: some Decided, some not
      if (decidedCount > 0 && notDecidedCount > 0) {
        partialDecisionRFQs++;
      }
    }

    return { totalLines, quotedLines, partialDecisionRFQs, agingLines };
  }, [openRFQs]);

  if (!kpis) return <Shell title="Dashboard"><div className="empty">Loading…</div></Shell>;

  const alerts = kpis.alerts || {};
  const pendingOverdue48 = alerts.pendingOverdue48 ?? alerts.pendingOverdue ?? 0;
  const pendingOverdue96 = alerts.pendingOverdue96 ?? 0;
  const idle2d = alerts.idle2d ?? alerts.idleRfqs ?? 0;
  const idle96h = alerts.idle96h ?? 0;

  return (
    <Shell title="Dashboard" subtitle={`${kpis.total} RFQs tracked`}>
      {/* Funnel — each status count is a link to filtered Inbox */}
      <div className="summary-strip" style={{ gridTemplateColumns: `repeat(${ORDER.length}, 1fr)` }}>
        {ORDER.map((s) => (
          <Link
            key={s}
            to={`/inbox?status=${s}`}
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            <div className="stat" style={{ cursor: 'pointer' }} title={`View ${s} RFQs`}>
              <div className="v">{kpis.funnel[s] || 0}</div>
              <div className="k">{s}</div>
            </div>
          </Link>
        ))}
      </div>

      <div className="row">
        {/* Aging alerts — two stacked tiers */}
        <div className="card">
          <h3 className="card-title">Aging alerts</h3>

          {/* 96h escalation tier */}
          {pendingOverdue96 > 0 && (
            <div style={{ marginBottom: 6 }}>
              <Link to="/inbox?status=AwaitingQuotes&aging=96" style={{ textDecoration: 'none' }}>
                <span className="chip risk">⚠ {pendingOverdue96}</span>
              </Link>
              {' '}supplier responses escalated &gt; 96h
            </div>
          )}
          {idle96h > 0 && (
            <div style={{ marginBottom: 6 }}>
              <Link to="/inbox?aging=96" style={{ textDecoration: 'none' }}>
                <span className="chip risk">⚠ {idle96h}</span>
              </Link>
              {' '}RFQs critically idle &gt; 4 days
            </div>
          )}

          {/* 48h warning tier */}
          <div style={{ marginBottom: 6 }}>
            <Link to="/inbox?status=AwaitingQuotes&aging=48" style={{ textDecoration: 'none' }}>
              <span className="chip review">{pendingOverdue48}</span>
            </Link>
            {' '}supplier responses pending &gt; 48h
          </div>
          <div>
            <Link to="/inbox?aging=48" style={{ textDecoration: 'none' }}>
              <span className="chip review">{idle2d}</span>
            </Link>
            {' '}RFQs idle &gt; 2 days
          </div>
        </div>

        {/* Win rate */}
        <div className="card">
          <h3 className="card-title">Win rate</h3>
          <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--ink-900)' }}>
            {kpis.winRate == null ? '—' : Math.round(kpis.winRate * 100) + '%'}
          </div>
          <div className="subtitle">Over all decided RFQs</div>
        </div>

        {/* Ready to compare but not decided */}
        <div className="card">
          <h3 className="card-title">Ready — awaiting decision</h3>
          <Link to="/inbox?status=ReadyForComparison" style={{ textDecoration: 'none' }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--ink-900)' }}>
              {kpis.readyNotDecided ?? 0}
            </div>
          </Link>
          <div className="subtitle">PartialQuotes + ReadyForComparison</div>
        </div>
      </div>

      {/* Phase F: Line-level KPI tiles */}
      {lineKpis && (
        <div className="row" style={{ marginTop: 16 }}>
          {/* Lines quoted tile */}
          <div className="card">
            <h3 className="card-title">Lines quoted</h3>
            <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--ink-900)' }}>
              {lineKpis.quotedLines} <span style={{ fontSize: 16, fontWeight: 400, color: 'var(--ink-500)' }}>/ {lineKpis.totalLines}</span>
            </div>
            <div className="subtitle">Across open RFQs</div>
            {lineKpis.totalLines > 0 && (
              <div style={{ marginTop: 8, height: 6, background: 'var(--border-1)', borderRadius: 3 }}>
                <div
                  style={{
                    height: '100%',
                    width: `${Math.round((lineKpis.quotedLines / lineKpis.totalLines) * 100)}%`,
                    background: 'var(--accent)',
                    borderRadius: 3,
                    transition: 'width 0.4s',
                  }}
                />
              </div>
            )}
          </div>

          {/* Partial-decision RFQs tile */}
          <div className="card">
            <h3 className="card-title">Partial-decision RFQs</h3>
            <div style={{ fontSize: 28, fontWeight: 700, color: lineKpis.partialDecisionRFQs > 0 ? 'var(--warning)' : 'var(--ink-900)' }}>
              {lineKpis.partialDecisionRFQs}
            </div>
            <div className="subtitle">RFQs with some lines decided, others not</div>
          </div>

          {/* Aging lines tile */}
          <div className="card">
            <h3 className="card-title">Lines awaiting quote &gt;48h</h3>
            <div style={{ fontSize: 28, fontWeight: 700, color: lineKpis.agingLines.length > 0 ? 'var(--danger)' : 'var(--ink-900)' }}>
              {lineKpis.agingLines.length}
            </div>
            {lineKpis.agingLines.length > 0 ? (
              <div style={{ marginTop: 8, maxHeight: 80, overflowY: 'auto' }}>
                {lineKpis.agingLines.slice(0, 5).map(({ rfq, line, ageH }) => (
                  <div key={line._id} style={{ fontSize: 12, marginBottom: 3 }}>
                    <Link to={`/rfq/${rfq._id}/responses`} style={{ color: 'var(--accent)' }}>
                      {rfq.rfqId || rfq._id}
                    </Link>
                    {' '}#{line.lineNumber} {line.partNumber}
                    <span className="chip risk" style={{ fontSize: 10, marginLeft: 4 }}>{ageH}h</span>
                  </div>
                ))}
                {lineKpis.agingLines.length > 5 && (
                  <div style={{ fontSize: 11, color: 'var(--ink-500)' }}>+{lineKpis.agingLines.length - 5} more</div>
                )}
              </div>
            ) : (
              <div className="subtitle">No lines aging</div>
            )}
          </div>
        </div>
      )}

      {/* Supplier leaderboard */}
      {kpis.supplierLeaderboard && kpis.supplierLeaderboard.length > 0 && (
        <div className="card" style={{ marginTop: 16 }}>
          <h3 className="card-title">
            Supplier scorecard{' '}
            <span style={{ fontWeight: 400, fontSize: 12, color: 'var(--ink-500)' }}>last 30 days</span>
          </h3>
          {/* Ranked on a composite, not response rate (#17). A supplier sent one
              envelope that replied once used to score 100% and top this table;
              small samples are now shrunk toward the average. */}
          <div className="subtitle" style={{ marginBottom: 8 }}>
            Ranked on responsiveness, quality, win rate, lead time and certification.
            Thin records are marked — a single reply is not a track record.
          </div>
          <table className="table" style={{ marginTop: 8 }}>
            <thead>
              <tr>
                <th>#</th>
                <th>Supplier</th>
                <th>Score</th>
                <th>Sent</th>
                <th>Response</th>
                <th>Quality</th>
                <th>Win</th>
                <th>Lead</th>
                <th>Approval</th>
                <th>AS9120</th>
              </tr>
            </thead>
            <tbody>
              {kpis.supplierLeaderboard.map((s, i) => {
                const pct = (v) => (v == null ? <span style={{ color: 'var(--ink-500)' }}>—</span> : `${v}%`);
                const certChip = { Valid: 'ready', ExpiringSoon: 'review', Expired: 'risk', Missing: 'muted' };
                const apprChip = {
                  Approved: 'ready', Conditional: 'review', Probation: 'review',
                  Suspended: 'risk', Unapproved: 'muted',
                };
                const c = s.components || {};
                return (
                  <tr key={String(s.supplierId)}>
                    <td style={{ color: 'var(--ink-500)', width: 28 }}>{i + 1}</td>
                    <td>
                      <Link to={`/suppliers/${s.supplierId}`}>{s.name}</Link>
                      {s.isThinEvidence && (
                        <span className="chip muted" style={{ fontSize: 10, marginLeft: 6 }} title={`Only ${s.sampleSize} envelope(s) — too little evidence to rank confidently`}>
                          thin
                        </span>
                      )}
                    </td>
                    <td
                      className="mono"
                      title={`responsiveness ${c.responsiveness} · quality ${c.quality} · win ${c.winRate} · lead ${c.leadTime} · cert ${c.certification}`}
                    >
                      <b>{s.composite}</b>
                    </td>
                    <td>{s.sent}</td>
                    <td>{pct(s.responseRate)}</td>
                    <td>{pct(s.qualityRate)}</td>
                    <td>{pct(s.winRate)}</td>
                    <td className="mono">{s.avgLeadDays == null ? '—' : `${s.avgLeadDays}d`}</td>
                    <td>
                      <span className={'chip ' + (apprChip[s.approvalStatus] || 'muted')} style={{ fontSize: 11 }}>
                        {s.approvalStatus}
                      </span>
                    </td>
                    <td>
                      <span
                        className={'chip ' + (certChip[s.certification?.state] || 'muted')}
                        style={{ fontSize: 11 }}
                        title={s.certification?.label}
                      >
                        {s.certification?.state === 'Missing' ? 'none' : s.certification?.state}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Shell>
  );
}
